import { z } from 'zod';
import { idnoSchema, registrarKindSchema } from './domain';

export const medicineCodeKindSchema = z.enum(['AUTHORIZED', 'UNAUTHORIZED', 'BARCODE']);
export type MedicineCodeKind = z.infer<typeof medicineCodeKindSchema>;

export const geographyKindSchema = z.enum([
  'COUNTRY',
  'REGION',
  'RAION',
  'MUNICIPALITY',
  'LOCALITY',
]);
export type GeographyKind = z.infer<typeof geographyKindSchema>;

export const geographySchema = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  kind: geographyKindSchema,
  cuatmCode: z.string().min(1).max(20).nullable(),
  name: z.string().min(1).max(200),
  path: z.string().min(1),
  active: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type Geography = z.infer<typeof geographySchema>;

export const upsertGeographyInputSchema = z.object({
  id: z.string().uuid().optional(),
  parentId: z.string().uuid().nullable(),
  kind: geographyKindSchema,
  cuatmCode: z.string().min(1).max(20).nullable().optional(),
  name: z.string().min(1).max(200),
  active: z.boolean().optional(),
  expectedRowVersion: z.number().int().nonnegative().optional(),
});
export type UpsertGeographyInput = z.infer<typeof upsertGeographyInputSchema>;

export const moveSubtreeInputSchema = z.object({
  id: z.string().uuid(),
  newParentId: z.string().uuid().nullable(),
  expectedRowVersion: z.number().int().nonnegative().optional(),
});
export type MoveSubtreeInput = z.infer<typeof moveSubtreeInputSchema>;

// The provenance of a nomenclator entry or of one of its codes: the two feeds
// (caiet UC20-01/02), a manual import (UC20-06) or a manual edit (UC20-11).
export const medicineSourceSchema = z.enum([
  'NOMENCLATOR_AUTORIZAT',
  'NOMENCLATOR_NEAUTORIZAT',
  'MANUAL_IMPORT',
  'MANUAL_EDIT',
]);
export type MedicineSource = z.infer<typeof medicineSourceSchema>;

// The descriptive attributes caiet UC50 step 4 makes searchable. They sit
// beside the "medicament unic" quintet, never inside it — and they are facts of a
// registration, so `catalog.medicine_code` carries them too and wins when it has one
// (docs/shape-uc-20-brand-per-code.md §3). Both tables use these widths.
//
// `manufacturer` is 500, not 200. Migration 1700000007000 widened the column for the
// producer list the register serves at 427 characters, and this bound stayed at the old
// width — the drift medicine-contract.spec.ts exists to catch, found while moving the
// field onto the code.
export const medicineDescriptiveAttributesSchema = z.object({
  commercialName: z.string().max(300).nullable(),
  manufacturer: z.string().max(500).nullable(),
  manufacturerCountry: z.string().max(100).nullable(),
  authorizationExpiresOn: z.string().nullable(),
});
export type MedicineDescriptiveAttributes = z.infer<typeof medicineDescriptiveAttributesSchema>;

// The bounds below are the database's, and they are checked against it by
// medicine-contract.spec.ts. Three of them are not what they look like:
//
//   dose has NO min(1). The AMDM authorised register serves 279 of its 6274 medicines
//   without one (spec 016). A `min(1)` here is the same defect that took the deployed
//   nomenclator sync to 0 records: a boundary refusing what the column accepts.
//
//   pharmaceuticalForm and division are 300, not 100. The register serves a 131- and a
//   124-character value, and both fields are components of the medicine natural key, so
//   a shorter bound cannot be met by truncating: that would change which medicine the
//   row is.
//
// dci, code, volume and the descriptive attributes keep their own column widths.
export const medicineSchema = medicineDescriptiveAttributesSchema.extend({
  id: z.string().uuid(),
  dci: z.string().min(1).max(200),
  pharmaceuticalForm: z.string().min(1).max(300),
  dose: z.string().max(100),
  volume: z.string().max(50).nullable(),
  division: z.string().max(300).nullable(),
  atcCode: z.string().max(20).nullable(),
  source: medicineSourceSchema,
  active: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type Medicine = z.infer<typeof medicineSchema>;

export const authorizationStatusSchema = z.enum(['AUTHORIZED', 'UNAUTHORIZED']);
export type AuthorizationStatus = z.infer<typeof authorizationStatusSchema>;

// A registration, and everything that is true of it rather than of the substance. The
// commercial name and the producer are among those: 743 of the 5004 positions the live
// register resolves to carry more than one code and 727 of them more than one brand.
// `authorizationExpiresOn` arrives with them — UC20-10 counts its configurable grace
// period from that date.
// The fields an administrator may claim against the nomenclator feed, and the only
// values `catalog.medicine_code.admin_owned_fields` ever holds. A varchar[] has no
// referential integrity, so this list is what binds the array to the columns.
// docs/shape-uc-20-provenance-and-holder.md §2.
export const medicineCodeAdminOwnedFieldSchema = z.enum([
  'commercialName',
  'manufacturer',
  'manufacturerCountry',
  'authorizationHolder',
  'authorizationHolderCountry',
  'authorizationExpiresOn',
]);
export type MedicineCodeAdminOwnedField = z.infer<typeof medicineCodeAdminOwnedFieldSchema>;

export const medicineCodeSchema = medicineDescriptiveAttributesSchema.extend({
  code: z.string().min(1).max(40),
  // Who answers legally for the product, and where. Code-only: `catalog.medicine` has no
  // holder, so unlike the three above there is no medicine-level value to fall back to.
  // 300 because it is one organisation name — the longest over all 6274 live authorised
  // records is 88 — and not 500, which `manufacturer` carries only because it is a
  // `;`-separated list of every production site.
  authorizationHolder: z.string().max(300).nullable(),
  authorizationHolderCountry: z.string().max(100).nullable(),
  adminOwnedFields: z.array(medicineCodeAdminOwnedFieldSchema),
  medicineId: z.string().uuid(),
  source: medicineSourceSchema,
  kind: medicineCodeKindSchema,
  authorizationStatus: authorizationStatusSchema,
  externalId: z.string().max(80).nullable(),
  lastSeenInSyncAt: z.string(),
  staleSince: z.string().nullable(),
  active: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type MedicineCode = z.infer<typeof medicineCodeSchema>;

export const medicineSearchRequestSchema = z.object({
  dciQuery: z.string().max(200).optional(),
  atcPrefix: z.string().max(20).optional(),
  onlyActive: z.boolean().optional(),
  limit: z.number().int().min(1).max(500).optional(),
  offset: z.number().int().min(0).optional(),
});
export type MedicineSearchRequest = z.infer<typeof medicineSearchRequestSchema>;

export const upsertMedicineInputSchema = medicineDescriptiveAttributesSchema.partial().extend({
  id: z.string().uuid().optional(),
  dci: z.string().min(1).max(200),
  pharmaceuticalForm: z.string().min(1).max(300),
  dose: z.string().max(100),
  volume: z.string().max(50).nullable(),
  division: z.string().max(300).nullable(),
  atcCode: z.string().max(20).nullable(),
  source: medicineSourceSchema.optional(),
  active: z.boolean().optional(),
  expectedRowVersion: z.number().int().nonnegative().optional(),
});
export type UpsertMedicineInput = z.infer<typeof upsertMedicineInputSchema>;

export const upsertMedicineCodeInputSchema = medicineDescriptiveAttributesSchema.partial().extend({
  code: z.string().min(1).max(40),
  dci: z.string().min(1).max(200),
  pharmaceuticalForm: z.string().min(1).max(300),
  dose: z.string().max(100),
  volume: z.string().max(50).nullable(),
  division: z.string().max(300).nullable(),
  atcCode: z.string().max(20).nullable(),
  source: medicineSourceSchema,
  kind: medicineCodeKindSchema.optional(),
  authorizationStatus: authorizationStatusSchema,
  externalId: z.string().max(80).nullable(),
});
export type UpsertMedicineCodeInput = z.infer<typeof upsertMedicineCodeInputSchema>;

// UC20-11 for a single code: add one, correct one in place, or flip it.
export const upsertMedicineCodeSchema = z.object({
  medicineId: z.string().uuid(),
  code: z.string().min(1).max(40),
  kind: medicineCodeKindSchema,
  authorizationExpiresOn: z.string().nullable(),
  previousCode: z.string().min(1).max(40).optional(),
});
export type UpsertMedicineCode = z.infer<typeof upsertMedicineCodeSchema>;

export const registrarSchema = z.object({
  id: z.string().uuid(),
  idno: idnoSchema,
  code: z.string().min(1).max(40),
  type: registrarKindSchema,
  name: z.string().min(1).max(200),
  active: z.boolean(),
});
export type Registrar = z.infer<typeof registrarSchema>;

export const siteSchema = z.object({
  id: z.string().uuid(),
  registrarId: z.string().uuid(),
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(200),
  geographyId: z.string().uuid(),
  active: z.boolean(),
});
export type Site = z.infer<typeof siteSchema>;
